import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Formik, Form, FormikHelpers } from 'formik';
import { observer, inject } from 'mobx-react';
import merge from 'lodash.merge';

import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import createStyles from '@material-ui/core/styles/createStyles';

import compose from 'helpers/compose';
import GridSubtitle from 'components/GridSubtitle/GridSubtitle';
import CustomPaper from 'components/Layout/Background';
import FormTextField from 'components/FormFields/FormTextField';
import FormSelectField from 'components/FormFields/FormSelectField';
import ControlButtons from 'components/ControlButtons';
import Message, { MessageVariant } from 'components/Message/Message';

import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { TransferTargetStore } from 'stores/account/transfer/transferTargetStore';
import { TransferTargetItem, TransferTargetData, initialValuesTarget } from 'stores/account/transfer/model';
import { RootStore } from 'stores/rootStore';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { formatAmountValue } from 'helpers/helpers';
import { borderRadiusBox } from 'components/AppTheme';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      padding: theme.spacing(0,1),
    },
    list: {
      //boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
      border: 'solid #ccc 1px',
      borderRadius: borderRadiusBox,
      width: 'calc(100% + 32px)',
      margin: theme.spacing(1),
      //padding: '8px 16px !important'
      '& > div': { padding: '8px 16px !important' }
    },
    header: {
      '& h2': { marginBottom: 0 },
      '& h2 span': { fontSize: '14px' },
      //marginLeft: '-16px',
      borderBottom: 'solid #ccc 1px',
      marginBottom: '16px',
      flexBasis: 'calc(100% + 16px)',
      maxWidth: 'calc(100% + 16px)',
    },
    item: {
      fontWeight: 'bold',
      alignItems: 'center',
      //margin: 0,
      borderBottom: 'solid #ccc 1px',
      marginBottom: '16px',
      flexBasis: 'calc(100% + 16px)',
      maxWidth: 'calc(100% + 16px)',
      '&:last-child': { borderBottom: 'none', marginBottom: 0 },
    },
  }),
);

interface Props {
  rootStore: RootStore;
  accountDetailStore: AccountDetailStore;
}

const targets = ['K1', 'K2', 'K3'];
const cash = ['CASH'];

const TransferTarget: React.FC<Props> = ({ rootStore, accountDetailStore }) => {
  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const [store] = useState(new TransferTargetStore(rootStore, accountDetailStore, accountId));
  const { fetchTargetData, modelTransfers, data } = store;
  const classes = useStyles();

  useEffect(() => {
    (async () => {
      await modelTransfers();
      await fetchTargetData();
    })();
  }, [accountId, fetchTargetData, modelTransfers]);

  const handleSubmit = (values: TransferTargetData, formikHelpers: FormikHelpers<TransferTargetData>) => {
    store.handleNext(values, formikHelpers);
  };

  const TransferItem = (item: TransferTargetItem, index: number) => {
    const selection = item.to === 'CASH' ? cash : targets;
    return (
      <Grid container item xs={12} className={classes.item} key={item.orderId}>
        <Grid item xs={3}>
          {t(`accountDetail_transfer_${item.from}`)}
        </Grid>
        <Grid item xs={6}>
          <FormSelectField
            name={`items[${index}].to`}
            label={t(
              item.enabled
                ? 'accountDetail_transferTarget_selectTargetPortfolio'
                : 'accountDetail_transferTarget_targetPortfolio',
            )}
            options={selection.map((target) => ({
              value: target,
              label:
                t(`accountDetail_transfer_${target}`) +
                ' (' +
                t(`accountDetail_transferTarget_account${item.target ? 'To' : 'From'}`) +
                ')',
            }))}
            disabled={!item.enabled}
          />
        </Grid>
        <Grid item xs={3} style={{ textAlign: 'center' }}>
          {item.value
            ? formatAmountValue(item.value.value, item.value.currency)
            : t('accountDetail_overview_movement_all')}
        </Grid>
      </Grid>
    );
  };

  return data?.items ? (
    <>
      <Formik initialValues={merge({}, initialValuesTarget, data)} onSubmit={handleSubmit}>
        {({ values, isSubmitting }) => {
          return (
            <Form id="transfer">
              <Container maxWidth="md">
                <CustomPaper>
                  <Grid container className={classes.wrapper}>
                    <GridSubtitle typographyProps={{ variant: 'h3' }}>
                      {t('accountDetail_transferTarget_account')}
                    </GridSubtitle>

                    <Grid item xs={12}>
                      <FormTextField label={t('accountDetail_transfer_toAccount')} name="targetAccountTitle" disabled />
                    </Grid>

                    <Grid item xs={12}>
                      {t('accountDetail_transferTarget_transferAll')}:{' '}
                      <strong>{t(values.transferAll ? 'general_yes' : 'general_no').toUpperCase()}</strong>
                    </Grid>

                    <Grid item xs={12}>
                    <Message
                      id="accountDetail_transfer_info"
                      message={t('accountDetail_transfer_info')}
                      variant={MessageVariant.INFO}
                      permanent
                    />
                    </Grid>

                    <GridSubtitle typographyProps={{ variant: 'h3' }}>
                      {t('accountDetail_transferTarget_transfers')}
                    </GridSubtitle>

                    <Grid container item xs={12} className={classes.list}>
                      <Grid container item xs={12} className={classes.header}>
                        <Grid item xs={3}>
                          <Typography variant="h2">
                            {t('accountDetail_transferTarget_fromTitle')}
                            <br />
                            <span>({t('accountDetail_transfer_sourceAccount')})</span>
                          </Typography>
                        </Grid>
                        <Grid item xs={6} style={{ textAlign: 'center' }}>
                          <Typography variant="h2">{t('accountDetail_transferTarget_to')}</Typography>
                        </Grid>
                        <Grid item xs={3} style={{ textAlign: 'center' }}>
                          <Typography variant="h2">{t('general_amount')}</Typography>
                        </Grid>
                      </Grid>
                      {data.items.map((item, i) => TransferItem(item, i))}
                    </Grid>
                  </Grid>
                </CustomPaper>
              </Container>

              <ControlButtons
                isSubmitting={isSubmitting}
                onBack={() => accountDetailStore.goBack()}
                backText={t(accountDetailStore.prevStepLabel)}
                nextText={t(accountDetailStore.nextStepLabel)}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(TransferTarget);
